import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, Plus, RotateCcw, Trash2 } from "lucide-react";
import {
  CATEGORIAS_COMISION,
  CATEGORIAS_GASTO,
  cargoActivo,
  esCargoInicial,
  esCargoPena,
  esCargoPeriodo,
  detalleDeCargo,
  fmtMXN,
  nuevoId,
  resumenCargosDeCondiciones,
  type BaseCalculo,
  type Cargo,
  type DetalleCargo,
  type Periodicidad,
  type TipoCargo,
  type TipoImporte,
} from "@/lib/operativo";
import { useOperativo } from "@/lib/operativo-store";
import { Seccion } from "@/components/shell/Seccion";

const PERIODICIDADES: Periodicidad[] = [
  "Única vez",
  "A la fecha de firma",
  "Por evento de mora",
  "Mensual",
  "Por período",
];
const BASES: BaseCalculo[] = ["Monto financiado (circular)", "Importe fijo"];

function clasificacion(c: Cargo) {
  if (esCargoPena(c)) return "Pena convencional (fuera de la tabla y del CAT)";
  if (c.financiado && esCargoInicial(c)) return "Financiado → monto financiado";
  if (!c.financiado && esCargoInicial(c)) return "Inicial no financiado → desembolso neto y CAT";
  if (esCargoPeriodo(c)) return "Por período → pago total de cada período y CAT";
  return "Sin impacto en la tabla";
}

/** Editor de un cargo de la solicitud (copia del producto). */
function EditorCargo({
  c,
  detalle,
  onChange,
  onDelete,
}: {
  c: Cargo;
  detalle: DetalleCargo | undefined;
  onChange: (patch: Partial<Cargo>) => void;
  onDelete: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const activo = cargoActivo(c);

  return (
    <div
      className={`rounded-lg border ${activo ? "border-navy/15" : "border-dashed border-muted"}`}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="min-w-0 text-left"
        >
          <p
            className={`truncate text-sm font-medium ${
              activo ? "text-navy" : "text-muted-foreground line-through"
            }`}
          >
            {c.nombre}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {c.tipo} · {c.categoria} · {c.tipoImporte === "%" ? `${c.importe}%` : fmtMXN(c.importe)}{" "}
            · {c.periodicidad}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">{clasificacion(c)}</p>
          {detalle && detalle.pct !== undefined && (
            <p className="mt-0.5 num text-xs text-muted-foreground">
              {detalle.pct}% sobre {fmtMXN(detalle.base)} = {fmtMXN(detalle.bruto)}
              {detalle.iva > 0 ? ` + IVA ${fmtMXN(detalle.iva)}` : ""}
            </p>
          )}
        </button>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className="num text-sm font-semibold text-navy">{fmtMXN(detalle?.total ?? 0)}</span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="no-print h-8 px-2"
              onClick={() => setAbierto((v) => !v)}
            >
              Editar
              <ChevronDown className={`ml-1 h-3.5 w-3.5 ${abierto ? "rotate-180" : ""}`} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="no-print h-8 px-2 text-destructive hover:text-destructive"
              onClick={onDelete}
              aria-label={`Eliminar ${c.nombre}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {abierto && (
        <div className="grid gap-3 border-t px-4 py-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Tipo</Label>
            <Select
              value={c.tipo}
              onValueChange={(v) =>
                onChange({
                  tipo: v as TipoCargo,
                  categoria: v === "Comisión" ? CATEGORIAS_COMISION[0]! : CATEGORIAS_GASTO[0]!,
                })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Comisión">Comisión</SelectItem>
                <SelectItem value="Gasto">Gasto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Categoría CONDUSEF</Label>
            <Select value={c.categoria} onValueChange={(v) => onChange({ categoria: v })}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(c.tipo === "Comisión" ? CATEGORIAS_COMISION : CATEGORIAS_GASTO).map((k) => (
                  <SelectItem key={k} value={k}>
                    {k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Nombre</Label>
            <Input
              className="h-9"
              value={c.nombre}
              onChange={(e) => onChange({ nombre: e.target.value.slice(0, 80) })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Base de cálculo</Label>
            <Select value={c.base} onValueChange={(v) => onChange({ base: v as BaseCalculo })}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BASES.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tipo de importe</Label>
            <Select
              value={c.tipoImporte}
              onValueChange={(v) => onChange({ tipoImporte: v as TipoImporte })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="%">% (porcentaje)</SelectItem>
                <SelectItem value="$">$ fijo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Importe</Label>
            <Input
              type="number"
              inputMode="decimal"
              step={0.01}
              className="h-9 num"
              value={Number.isNaN(c.importe) ? "" : c.importe}
              onChange={(e) => onChange({ importe: e.target.valueAsNumber })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Periodicidad</Label>
            <Select
              value={c.periodicidad}
              onValueChange={(v) => onChange({ periodicidad: v as Periodicidad })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODICIDADES.map((k) => (
                  <SelectItem key={k} value={k}>
                    {k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Momento de aplicación</Label>
            <Input
              className="h-9"
              value={c.momento}
              onChange={(e) => onChange({ momento: e.target.value.slice(0, 60) })}
            />
          </div>
          <div className="flex flex-wrap items-center gap-6 sm:col-span-2 xl:col-span-4">
            <div className="flex items-center gap-2">
              <Switch
                id={`${c.id}-sol-apl`}
                checked={activo}
                onCheckedChange={(v) => onChange({ aplicar: v })}
              />
              <Label htmlFor={`${c.id}-sol-apl`} className="text-xs">
                ¿Aplicar? {activo ? "Sí" : "No (calcula 0)"}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id={`${c.id}-sol-fin`}
                checked={c.financiado}
                onCheckedChange={(v) => onChange({ financiado: v })}
              />
              <Label htmlFor={`${c.id}-sol-fin`} className="text-xs">
                ¿Financiado? {c.financiado ? "Sí" : "No (cobro externo)"}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id={`${c.id}-sol-iva`}
                checked={c.causaIva}
                onCheckedChange={(v) => onChange({ causaIva: v })}
              />
              <Label htmlFor={`${c.id}-sol-iva`} className="text-xs">
                ¿Causa IVA? {c.causaIva ? "Sí" : "No"}
              </Label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Bloque colapsable "Comisiones y Gastos del crédito" dentro de la solicitud. */
export function CargosSolicitud() {
  const {
    producto,
    condiciones,
    solicitud,
    cargosCredito,
    setCargosCredito,
    restaurarCargosDelProducto,
  } = useOperativo();
  const [abierta, setAbierta] = useState(true);

  const propios = cargosCredito.filter((c) => c.productoId === producto.id);
  const monto = Number.isFinite(solicitud.monto) ? solicitud.monto : 0;
  const res = resumenCargosDeCondiciones(condiciones, cargosCredito, producto.id);

  const set = (id: string, patch: Partial<Cargo>) =>
    setCargosCredito(cargosCredito.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const agregar = () =>
    setCargosCredito([
      ...cargosCredito,
      {
        id: nuevoId("cargo-sol"),
        productoId: producto.id,
        tipo: "Gasto",
        categoria: CATEGORIAS_GASTO[0]!,
        nombre: "Nuevo cargo del crédito",
        base: "Importe fijo",
        tipoImporte: "$",
        importe: 0,
        periodicidad: "Única vez",
        momento: "Al desembolso",
        financiado: false,
        causaIva: true,
        aplicar: true,
      },
    ]);

  return (
    <Seccion
      titulo="Comisiones y Gastos del crédito"
      abierta={abierta}
      onToggle={() => setAbierta((v) => !v)}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-2xl text-xs text-muted-foreground">
            Copia editable de la plantilla de <span className="text-navy">{producto.nombre}</span>.
            Los cambios aplican solo a este crédito y recalculan en vivo el monto financiado, el
            desembolso neto, la cuota, el pago total por período y el CAT. La plantilla del producto
            no se modifica.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="no-print"
              onClick={restaurarCargosDelProducto}
            >
              <RotateCcw className="mr-1.5 h-4 w-4" /> Restaurar del producto
            </Button>
            <Button size="sm" variant="secondary" className="no-print" onClick={agregar}>
              <Plus className="mr-1.5 h-4 w-4" /> Agregar cargo
            </Button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { l: "Financiados", v: res.financiados },
            { l: "Iniciales no financiados", v: res.inicialesNoFinanciados },
            { l: "Por período", v: res.porPeriodo },
            { l: "Pena convencional (manual)", v: res.penas },
          ].map((k) => (
            <Card key={k.l} className="border-navy/15 bg-surface p-3 shadow-none">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {k.l}
              </p>
              <p className="mt-1 num text-lg font-semibold text-navy">{fmtMXN(k.v)}</p>
            </Card>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{res.activos} activos</Badge>
          <Badge variant="outline">{res.total} en la lista</Badge>
        </div>
        <p className="num rounded-md border border-navy/15 bg-surface px-3 py-2 text-xs text-muted-foreground">
          Monto solicitado {fmtMXN(monto)} + financiados {fmtMXN(res.financiados)} ={" "}
          <span className="font-semibold text-navy">
            monto financiado {fmtMXN(condiciones.montoFinanciado)}
          </span>
        </p>

        <div className="space-y-2">
          {propios.length === 0 && (
            <p className="rounded-md border border-dashed px-4 py-6 text-sm text-muted-foreground">
              Sin comisiones ni gastos en este crédito.
            </p>
          )}
          {propios.map((c) => (
            <EditorCargo
              key={c.id}
              c={c}
              detalle={detalleDeCargo(condiciones, c.id)}
              onChange={(patch) => set(c.id, patch)}
              onDelete={() => setCargosCredito(cargosCredito.filter((x) => x.id !== c.id))}
            />
          ))}
        </div>
      </div>
    </Seccion>
  );
}
